import { effect, inject, Injectable } from '@angular/core';
import { AuthUser } from '../domainTypes/auth';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

/** Feedback board root — the "Feedback" entry in the account menu. */
export const CANNY_BOARD_URL = 'https://collectiviq.canny.io/';
/** DDR-specific board — the alpha dialog and the DDR satisfaction banner. */
export const CANNY_DDR_FEEDBACK_URL = 'https://collectiviq.canny.io/ddr-feedback';

declare const Canny: (
    action: string,
    options: {
        appID: string;
        user: {
            email: string;
            id: string;
            name: string;
        };
        company?: {
            name: string;
            id: string;
        };
    }
) => void;

@Injectable({
    providedIn: 'root',
})
export class CannyService {
    private readonly authService = inject(AuthService);

    identifyUser$ = effect(() => {
        const authState = this.authService.state();

        if (authState.status !== 'authenticated' || !authState.user) {
            return;
        }

        this.identifyUser(authState.user);
    });

    /**
     * Open a Canny destination so the SDK can hand our identified user over.
     *
     * `Canny('identify', …)` is only half of the login handoff. The other half is
     * link authentication: identify installs capture-phase click/mousedown/focusin
     * listeners on `document.body` and, when the event's ancestor chain contains an
     * `<a>` pointing at Canny, rewrites that anchor's `href` to carry a
     * `clientToken` query param. Canny's site reads the token and the user lands
     * signed in.
     *
     * That means the handoff only happens for a **real anchor in the light DOM**:
     *  - `window.open(url)` dispatches no click at all, so nothing is rewritten
     *    (this is what regressed when the feedback anchor became a button);
     *  - `<wa-button href>` renders its `<a>` inside a shadow root, so the event is
     *    retargeted to the `wa-button` host and the SDK's walk up `parentElement`
     *    never finds an anchor.
     *
     * So build the anchor, click it, and drop it. `data-canny-link` is what the SDK
     * looks for first; without it the SDK falls back to matching `*.canny.io`
     * hostnames (its `authenticateLinks` default), which would break silently if the
     * board ever moves to a custom domain.
     *
     * Degrades to a plain new tab when the SDK is absent or identify was skipped
     * (local dev, blocked script): the anchor click still navigates, just without a
     * token — exactly today's behaviour.
     */
    openBoard(url: string = CANNY_BOARD_URL): void {
        const link = document.createElement('a');
        link.setAttribute('data-canny-link', '');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';

        // Must be in the document: the SDK's listeners are bound to document.body,
        // and a detached node's click event never reaches them.
        document.body.appendChild(link);
        try {
            link.click();
        } finally {
            link.remove();
        }
    }

    private identifyUser(user: AuthUser) {
        if (!environment.production && (environment.environmentType as string) !== 'feature1') {
            console.log('Skipping Canny identify call in development mode');
            return;
        }

        Canny('identify', {
            appID: '698f5fa96a9a190327ff0b30',
            user: {
                id: user.user_id.toString(),
                email: user.email,
                name: user.email,
            },
            company: {
                name: user.company_name ?? '',
                id: user.company_id?.toString() ?? '',
            },
        });
    }
}
